import './observability';

import { Suspense } from 'react';

import { createRoot } from 'react-dom/client';

import { Provider } from 'react-redux';
import { HelmetProvider } from 'react-helmet-async';
import FontFaceObserver from 'fontfaceobserver';
import { Router } from 'react-router-dom';

import { RecoilRoot } from 'recoil';

import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider, StyledEngineProvider } from '@mui/material/styles';

import '@fontsource/inter'; // 400
import '@fontsource/inter/500.css'; // Medium
import '@fontsource/inter/600.css'; // Semibold
import '@fontsource/inter/700.css'; // Bold
import '@fontsource/roboto';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/900-italic.css';
import '@fontsource/courier-prime';
import 'keen-slider/keen-slider.min.css';

import '@experimental-components/MaterialPickersCalendar/calendar.css';
import '@experimental-components/MaterialPickersDaterange/dateRangePicker.css';
import '@experimental-components/MaterialPickersDaterangeV2/dateRangePickerV2.css';
import '@experimental-components/MaterialPickersSingleDate/singleDatePicker.css';

import AmplitudeProvider from 'providers/root/AmplitudeProvider';
import NotificationProvider from '@design-system/providers/NotificationProvider';
import LocaleProvider from 'providers/root/LocaleProvider';
import AuthProvider from 'providers/root/AuthProvider';
import history from 'utils/history';
import theme from 'utils/theme';

import { QueryClient, QueryClientProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';

import App from './App';
import i18n from './i18n';
import configureStore from './configureStore';
import './styles/tailwind.css';

// Register domain-specific translations
import { registerPersonnelTranslations } from 'domains/personnel';
import { registerReportsTranslations } from 'domains/reports';
import { initIdentityComposition, registerIdentityTranslations } from 'domains/identity';
import { registerMfeTranslations } from 'mfe/locales';

registerPersonnelTranslations();
registerReportsTranslations();
registerIdentityTranslations();
registerMfeTranslations();

// Identity opera con y sin sesión: su composición NO puede colgar de bootstrapApp, que corre
// solo tras autenticar. Las rutas /auth/* son públicas.
initIdentityComposition();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

// Observe loading of Roboto (to remove 'Roboto', remove the <link> tag in
// the index.html file and this observer)
const openSansObserver = new FontFaceObserver('Roboto', {});

// When Roboto is loaded, add a font-family using Roboto to the body
openSansObserver.load().then(() => {
  document.body.classList.add('fontLoaded');
});

// Create redux store with history
const initialState = {};
const store = configureStore(initialState, history);
const MOUNT_NODE = document.getElementById('root');
const root = createRoot(MOUNT_NODE);
root.render(
  <QueryClientProvider client={queryClient}>
    <Provider store={store}>
      <RecoilRoot>
        <LocaleProvider i18n={i18n}>
          <Suspense
            fallback={
              <Backdrop open>
                <CircularProgress color="secondary" />
              </Backdrop>
            }
          >
            <Router history={history}>
              <HelmetProvider>
                <StyledEngineProvider injectFirst>
                  <ThemeProvider theme={theme}>
                    <CssBaseline />
                    <AmplitudeProvider>
                      <NotificationProvider>
                        <AuthProvider>
                          <App />
                        </AuthProvider>
                      </NotificationProvider>
                    </AmplitudeProvider>
                  </ThemeProvider>
                </StyledEngineProvider>
              </HelmetProvider>
            </Router>
          </Suspense>
        </LocaleProvider>
      </RecoilRoot>
    </Provider>
    <ReactQueryDevtools initialIsOpen={false} />
  </QueryClientProvider>,
);

if (module.hot) {
  module.hot.accept(['./i18n'], () => {
    // No need to render the App again because i18next works with the hooks
  });
}

if (process.env.REACT_APP_ENABLE_MOCK_SERVER === 'true') {
  const { worker } = require('./mocks/browser');
  worker.start();
}
